import { useRef, useCallback, forwardRef } from "react";
import { cn } from "@/lib/core/utils";

interface BentoFeatureCardProps {
  featureId: string;
  onFeatureClick: (featureId: string, rect: DOMRect) => void;
  className?: string;
  children: React.ReactNode;
}

export const BentoFeatureCard = forwardRef<HTMLDivElement, BentoFeatureCardProps>(
  ({ featureId, onFeatureClick, className, children }, ref) => {
    const internalRef = useRef<HTMLDivElement>(null);

    const mergedRef = useCallback(
      (node: HTMLDivElement | null) => {
        internalRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref)
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref]
    );

    const handleClick = () => {
      if (internalRef.current) {
        onFeatureClick(featureId, internalRef.current.getBoundingClientRect());
      }
    };

    return (
      <div
        ref={mergedRef}
        className={cn(className, "cursor-pointer")}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {children}
      </div>
    );
  }
);

BentoFeatureCard.displayName = "BentoFeatureCard";
